import {
	AppstoreOutlined,
	DeleteOutlined,
	EditOutlined,
	EllipsisOutlined,
	EyeInvisibleOutlined,
	EyeOutlined,
	FileImageOutlined,
	MenuOutlined,
	PlusOutlined,
	SettingOutlined,
	TeamOutlined,
} from "@ant-design/icons";
import { BaseAnimation } from "@di-devs/base-components";
import { useSystemSettings } from "@di-devs/system-settings-feature-plugin/client";
import { SYSTEM_SETTING_KEYS } from "@di-devs/system-settings-feature-plugin/shared";
import {
	App,
	Badge,
	Button,
	Card,
	Col,
	Divider,
	Dropdown,
	Empty,
	Image,
	Row,
	Space,
	Spin,
	Table,
	Tabs,
	Tag,
	Typography,
} from "antd";
import type React from "react";
import { useState } from "react";

const { Title, Text } = Typography;

import { useEnvStore } from "@di-devs/utils-fe";
import type { RichMenuTabGroupWithRichmenus } from "../../../../shared/types/richmenu.types";
import { getRichmenuTemplateImage } from "../../../assets";
import {
	useDeleteRichMenuTabGroup,
	usePublishRichMenuTabGroup,
	useRichMenuTabGroups,
	useUnpublishRichMenuTabGroup,
} from "../../../richmenu-tab-group/hooks/useRichmenuTabGroupQueries";
import { RichMenuModal } from "./RichMenuModal";

interface RichMenuSettingsProps {
	/**
	 * Public settings for the page header
	 */
	publicSettings?: any;
	/**
	 * API base URL
	 * @default '/api2'
	 */
	apiBaseUrl?: string;
	/**
	 * Whether user can manage all settings or just public ones
	 * @default false
	 */
	isAdmin?: boolean;
	/**
	 * Custom page title
	 */
	title?: string;
	/**
	 * Additional CSS class name
	 */
	className?: string;
	/**
	 * Callback when settings are updated
	 */
	onUpdate?: (settingName: string, value: any) => void;
}

export const RichMenuSettings: React.FC<RichMenuSettingsProps> = ({
	publicSettings,
	apiBaseUrl = "/api2",
	isAdmin = false,
	title = "リッチメニュー設定",
	className,
	onUpdate,
}) => {
	const { message: messageApi } = App.useApp();
	const { API_URL } = useEnvStore();
	const {
		tabGroups: tabGroupsData,
		loading: isLoading,
		refetch,
	} = useRichMenuTabGroups();

	// System settings for theme colors
	const systemSettingsResult = useSystemSettings({ apiBaseUrl });
	const {
		publicSettings: systemSettings,
		getSettingValue,
		loading: settingsLoading,
	} = systemSettingsResult;
	const [modalVisible, setModalVisible] = useState(false);
	const [editingTabGroup, setEditingTabGroup] =
		useState<RichMenuTabGroupWithRichmenus | null>(null);
	const [activeTabKey, setActiveTabKey] = useState("published");

	const publishMutation = usePublishRichMenuTabGroup();
	const unpublishMutation = useUnpublishRichMenuTabGroup();
	const deleteMutation = useDeleteRichMenuTabGroup();

	const tabGroups: RichMenuTabGroupWithRichmenus[] = tabGroupsData || [];

	// Theme colors from system settings
	const primaryColor =
		getSettingValue(SYSTEM_SETTING_KEYS.PRIMARY_COLOR) || "#99CA29";
	const primaryLightColor =
		getSettingValue(SYSTEM_SETTING_KEYS.PRIMARY_LIGHT_COLOR) || "#e6f4ff";
	const _secondaryColor =
		getSettingValue(SYSTEM_SETTING_KEYS.SECONDARY_COLOR) || "#52c41a";

	// Helper function for dropdown labels (matching @tmp/ style)
	const renderDropdownLabel = (icon: React.ReactNode, labelText: string) => (
		<>
			{icon}
			<span style={{ marginLeft: 8 }}>{labelText}</span>
		</>
	);

	// Dynamic image URL helper
	const getImageUrl = (imageName: string) => {
		// Rich menu images are stored in assets/public/richmenus/
		return `${API_URL || "http://localhost:8080"}/assets/public/richmenus/${imageName}`;
	};
	const defaultTabGroup = tabGroups.find(
		(g: RichMenuTabGroupWithRichmenus) =>
			g.displayPriority === "DEFAULT" && g.status === "PUBLISHED",
	);
	const perUserTabGroup = tabGroups.find(
		(g: RichMenuTabGroupWithRichmenus) =>
			g.displayPriority === "USER" && g.status === "PUBLISHED",
	);
	const draftTabGroups = tabGroups.filter(
		(g: RichMenuTabGroupWithRichmenus) => g.status === "DRAFT",
	);

	// Categorize drafts by display priority
	const _draftDefaultTabGroups = draftTabGroups.filter(
		(g: RichMenuTabGroupWithRichmenus) => g.displayPriority === "DEFAULT",
	);
	const _draftUserTabGroups = draftTabGroups.filter(
		(g: RichMenuTabGroupWithRichmenus) => g.displayPriority === "USER",
	);

	const handlePublish = async (tabGroupId: number) => {
		try {
			console.log("Publishing tab group:", tabGroupId);

			// Find the tab group being published for debugging
			const tabGroupToPublish = tabGroups.find((g) => g.groupId === tabGroupId);
			console.log("Tab group data being published:", {
				tabGroup: tabGroupToPublish,
				richmenus: tabGroupToPublish?.richmenus,
				richmenusCount: tabGroupToPublish?.richmenus?.length,
			});

			// Check for existing published tab group with same priority
			if (tabGroupToPublish) {
				const existingPublished = tabGroups.find(
					(g: RichMenuTabGroupWithRichmenus) =>
						g.displayPriority === tabGroupToPublish.displayPriority &&
						g.status === "PUBLISHED" &&
						g.groupId !== tabGroupId,
				);

				if (existingPublished) {
					messageApi.error(
						`同じ優先度の公開済みリッチメニューが既に存在します。先に「${existingPublished.name || "その他のリッチメニュー"}」を非公開にしてください。`,
					);
					return;
				}
			}

			const result = await publishMutation.mutateAsync(tabGroupId);
			console.log("Publish result:", result);
			messageApi.success("リッチメニューの公開が完了しました");
			refetch();
		} catch (error) {
			console.error("Publish error:", error);
			console.error("Full error details:", JSON.stringify(error, null, 2));
			messageApi.error(
				`リッチメニューの公開に失敗しました: ${error instanceof Error ? error.message : "Unknown error"}`,
			);
		}
	};

	const handleUnpublish = async (tabGroupId: number) => {
		try {
			console.log("Unpublishing tab group:", tabGroupId);
			const result = await unpublishMutation.mutateAsync(tabGroupId);
			console.log("Unpublish result:", result);
			messageApi.success("リッチメニューの非公開が完了しました");
			refetch();
		} catch (error) {
			console.error("Unpublish error:", error);
			messageApi.error(
				`リッチメニューの非公開に失敗しました: ${error instanceof Error ? error.message : "Unknown error"}`,
			);
		}
	};

	const handleDelete = async (tabGroupId: number) => {
		try {
			await deleteMutation.mutateAsync(tabGroupId);
			messageApi.success("リッチメニューの削除が完了しました");
			refetch();
		} catch (_error) {
			messageApi.error("リッチメニューの削除に失敗しました");
		}
	};

	const handleEdit = (tabGroup: RichMenuTabGroupWithRichmenus) => {
		setEditingTabGroup(tabGroup);
		setModalVisible(true);
	};

	const handleCreate = () => {
		setEditingTabGroup(null);
		setModalVisible(true);
	};

	const handleModalSuccess = (savedTabGroup?: any) => {
		refetch();
		setModalVisible(false);
		setEditingTabGroup(null);

		// Intelligently switch to the appropriate tab based on the saved item
		if (savedTabGroup) {
			if (savedTabGroup.status === "PUBLISHED") {
				setActiveTabKey("published");
			} else if (savedTabGroup.status === "DRAFT") {
				setActiveTabKey("drafts");
			}
		} else {
			// If we don't have the saved tab group info, stay on current tab or switch to drafts for new items
			if (!editingTabGroup) {
				// New item - likely a draft
				setActiveTabKey("drafts");
			}
			// For existing items, keep the current tab
		}
	};

	// Backend request handlers
	const handleDuplicate = async (tabGroup: RichMenuTabGroupWithRichmenus) => {
		try {
			messageApi.loading("リッチメニューを複製しています...", 0);

			// Create a duplicate with modified name
			const duplicateData = {
				name: `${tabGroup.name} (コピー)`,
				displayPriority: tabGroup.displayPriority,
				richmenus:
					tabGroup.richmenus?.map((richmenu) => ({
						...richmenu,
						id: undefined, // Remove ID to create new
						richMenuId: undefined,
						richMenuAliasId: undefined,
					})) || [],
			};

			// TODO: Replace with actual API call
			console.log("Duplicating rich menu tab group:", duplicateData);
			await new Promise((resolve) => setTimeout(resolve, 2000)); // Simulate API call

			messageApi.destroy();
			messageApi.success("リッチメニューを複製しました");
			refetch();
			setActiveTabKey("drafts");
		} catch (error) {
			messageApi.destroy();
			messageApi.error("リッチメニューの複製に失敗しました");
			console.error("Duplicate error:", error);
		}
	};

	const handleExport = async (tabGroup: RichMenuTabGroupWithRichmenus) => {
		try {
			messageApi.loading("データをエクスポートしています...", 0);

			// TODO: Replace with actual API call
			console.log("Exporting rich menu tab group:", tabGroup);
			await new Promise((resolve) => setTimeout(resolve, 1500)); // Simulate API call

			// Create and download JSON file
			const exportData = {
				name: tabGroup.name,
				displayPriority: tabGroup.displayPriority,
				richmenus: tabGroup.richmenus || [],
				exportedAt: new Date().toISOString(),
			};

			const dataStr = JSON.stringify(exportData, null, 2);
			const dataBlob = new Blob([dataStr], { type: "application/json" });
			const url = URL.createObjectURL(dataBlob);
			const link = document.createElement("a");
			link.href = url;
			link.download = `richmenu-${tabGroup.name}-${Date.now()}.json`;
			link.click();
			URL.revokeObjectURL(url);

			messageApi.destroy();
			messageApi.success("データをエクスポートしました");
		} catch (error) {
			messageApi.destroy();
			messageApi.error("エクスポートに失敗しました");
			console.error("Export error:", error);
		}
	};

	const handleAnalytics = async (tabGroup: RichMenuTabGroupWithRichmenus) => {
		try {
			messageApi.loading("分析データを取得しています...", 0);

			// TODO: Replace with actual API call
			console.log(
				"Fetching analytics for rich menu tab group:",
				tabGroup.groupId,
			);
			await new Promise((resolve) => setTimeout(resolve, 1000)); // Simulate API call

			messageApi.destroy();
			messageApi.info(
				`${tabGroup.name}の分析データ: クリック数 ${Math.floor(Math.random() * 1000)}, 表示回数 ${Math.floor(Math.random() * 5000)}`,
			);
		} catch (error) {
			messageApi.destroy();
			messageApi.error("分析データの取得に失敗しました");
			console.error("Analytics error:", error);
		}
	};

	const handlePreview = async (tabGroup: RichMenuTabGroupWithRichmenus) => {
		try {
			messageApi.loading("プレビューを生成しています...", 0);

			// TODO: Replace with actual API call
			console.log(
				"Generating preview for rich menu tab group:",
				tabGroup.groupId,
			);
			await new Promise((resolve) => setTimeout(resolve, 1200)); // Simulate API call

			messageApi.destroy();
			messageApi.success("プレビューを生成しました");
			// TODO: Open preview modal or new tab
		} catch (error) {
			messageApi.destroy();
			messageApi.error("プレビューの生成に失敗しました");
			console.error("Preview error:", error);
		}
	};

	// Individual rich menu handlers
	const handleRichMenuDuplicate = async (
		richmenu: any,
		tabGroup: RichMenuTabGroupWithRichmenus,
	) => {
		try {
			messageApi.loading("リッチメニューを複製しています...", 0);

			// TODO: Replace with actual API call
			console.log(
				"Duplicating individual rich menu:",
				richmenu.id,
				"in tab group:",
				tabGroup.groupId,
			);
			await new Promise((resolve) => setTimeout(resolve, 1500)); // Simulate API call

			messageApi.destroy();
			messageApi.success("リッチメニューを複製しました");
			refetch();
		} catch (error) {
			messageApi.destroy();
			messageApi.error("リッチメニューの複製に失敗しました");
			console.error("Rich menu duplicate error:", error);
		}
	};

	const handleRichMenuDownload = async (
		richmenu: any,
		tabGroup: RichMenuTabGroupWithRichmenus,
	) => {
		try {
			messageApi.loading("リッチメニューをダウンロードしています...", 0);

			// TODO: Replace with actual API call
			console.log(
				"Downloading rich menu:",
				richmenu.id,
				"from tab group:",
				tabGroup.groupId,
			);
			await new Promise((resolve) => setTimeout(resolve, 1200)); // Simulate API call

			// Create download data
			const downloadData = {
				richmenu: richmenu,
				tabGroupName: tabGroup.name,
				downloadedAt: new Date().toISOString(),
			};

			const dataStr = JSON.stringify(downloadData, null, 2);
			const dataBlob = new Blob([dataStr], { type: "application/json" });
			const url = URL.createObjectURL(dataBlob);
			const link = document.createElement("a");
			link.href = url;
			link.download = `richmenu-${richmenu.name || richmenu.id}-${Date.now()}.json`;
			link.click();
			URL.revokeObjectURL(url);

			messageApi.destroy();
			messageApi.success("リッチメニューをダウンロードしました");
		} catch (error) {
			messageApi.destroy();
			messageApi.error("ダウンロードに失敗しました");
			console.error("Rich menu download error:", error);
		}
	};

	const handleRichMenuDelete = async (
		richmenu: any,
		tabGroup: RichMenuTabGroupWithRichmenus,
	) => {
		try {
			messageApi.loading("リッチメニューを削除しています...", 0);

			// TODO: Replace with actual API call
			console.log(
				"Deleting rich menu:",
				richmenu.id,
				"from tab group:",
				tabGroup.groupId,
			);
			await new Promise((resolve) => setTimeout(resolve, 800)); // Simulate API call

			messageApi.destroy();
			messageApi.success("リッチメニューを削除しました");
			refetch();
		} catch (error) {
			messageApi.destroy();
			messageApi.error("リッチメニューの削除に失敗しました");
			console.error("Rich menu delete error:", error);
		}
	};

	const _renderTabGroupCard = (
		tabGroup: RichMenuTabGroupWithRichmenus,
		type: string,
	): React.ReactElement => {
		const getTypeIcon = (type: string) => {
			if (type.includes("Default")) return <AppstoreOutlined />;
			if (type.includes("User")) return <TeamOutlined />;
			return <MenuOutlined />;
		};

		const getStatusBadge = (status: string) => {
			if (status === "PUBLISHED") {
				return <Badge status="success" text="公開中" />;
			}
			return <Badge status="default" text="下書き" />;
		};

		const getPriorityTag = (priority: string) => {
			if (priority === "DEFAULT") {
				return (
					<Tag color="blue" icon={<AppstoreOutlined />}>
						デフォルト
					</Tag>
				);
			}
			return (
				<Tag color="green" icon={<TeamOutlined />}>
					ユーザー別
				</Tag>
			);
		};

		// Action dropdown items for @tmp/ style

		const publishedTableRowOperations = [
			{
				key: "unpublish",
				label: renderDropdownLabel(<EyeInvisibleOutlined />, "非公開"),
				onClick: () => handleUnpublish(tabGroup.groupId),
			},
			{
				key: "edit",
				label: renderDropdownLabel(<EditOutlined />, "編集"),
				onClick: () => handleEdit(tabGroup),
			},
		];

		const unpublishedTableRowOperations = [
			{
				key: "publish",
				label: renderDropdownLabel(<EyeOutlined />, "公開"),
				onClick: () => handlePublish(tabGroup.groupId),
			},
			{
				key: "edit",
				label: renderDropdownLabel(<EditOutlined />, "編集"),
				onClick: () => handleEdit(tabGroup),
			},
			{
				key: "duplicate",
				label: renderDropdownLabel(<AppstoreOutlined />, "複製"),
				onClick: () => handleDuplicate(tabGroup),
			},
			{
				key: "export",
				label: renderDropdownLabel(<SettingOutlined />, "エクスポート"),
				onClick: () => handleExport(tabGroup),
			},
			{
				key: "preview",
				label: renderDropdownLabel(<FileImageOutlined />, "プレビュー"),
				onClick: () => handlePreview(tabGroup),
			},
			{
				key: "analytics",
				label: renderDropdownLabel(<TeamOutlined />, "分析データ"),
				onClick: () => handleAnalytics(tabGroup),
			},
			{
				key: "delete",
				label: renderDropdownLabel(<DeleteOutlined />, "削除"),
				onClick: () => handleDelete(tabGroup.groupId),
				danger: true,
			},
		];

		return (
			<Card
				key={`${type}-${tabGroup.groupId}`}
				hoverable
				style={{
					borderRadius: 12,
					overflow: "hidden",
					boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
					transition: "all 0.3s ease",
					borderColor: primaryColor,
				}}
				styles={{
					header: {
						borderBottom: "1px solid #f0f0f0",
						background: primaryLightColor,
						color: primaryColor,
					},
					body: {
						padding: "16px",
					},
				}}
				title={
					<Space>
						{getTypeIcon(type)}
						<span style={{ fontWeight: 600 }}>{tabGroup.name}</span>
						{getPriorityTag(tabGroup.displayPriority)}
					</Space>
				}
				extra={
					<Space>
						{getStatusBadge(tabGroup.status)}
						<Dropdown
							menu={{
								items:
									tabGroup.status === "PUBLISHED"
										? publishedTableRowOperations
										: unpublishedTableRowOperations,
							}}
							trigger={["click"]}
							placement="bottomRight"
						>
							<Button
								type="text"
								icon={<EllipsisOutlined />}
								style={{
									borderRadius: 6,
									color: primaryColor,
								}}
							/>
						</Dropdown>
					</Space>
				}
			>
				{/* Rich Menu Thumbnails */}
				<div style={{ marginBottom: 20 }}>
					<div
						style={{
							marginBottom: 12,
							display: "flex",
							alignItems: "center",
							gap: 8,
						}}
					>
						<FileImageOutlined style={{ color: "#666" }} />
						<Text strong style={{ fontSize: 14 }}>
							リッチメニュー一覧
						</Text>
						<Badge
							count={tabGroup.richmenus?.length || 0}
							showZero
							style={{ backgroundColor: "#52c41a" }}
						/>
					</div>

					{tabGroup.richmenus && tabGroup.richmenus.length > 0 ? (
						<Table
							dataSource={tabGroup.richmenus}
							pagination={false}
							size="middle"
							style={{ marginTop: 8 }}
							rowKey={(record) => record.id || `menu-${Math.random()}`}
							scroll={{ x: true }}
							columns={[
								{
									title: "プレビュー",
									dataIndex: "imageName",
									key: "preview",
									width: 120,
									render: (imageName: string, record: any) => (
										<div
											style={{
												width: 100,
												height: 60,
												border: record.selected
													? "3px solid #52c41a"
													: "2px solid #e8e8e8",
												borderRadius: 8,
												backgroundColor: "#fafafa",
												display: "flex",
												alignItems: "center",
												justifyContent: "center",
												position: "relative",
												overflow: "hidden",
												boxShadow: record.selected
													? "0 2px 8px rgba(82, 196, 26, 0.3)"
													: "0 1px 4px rgba(0,0,0,0.1)",
											}}
										>
											{imageName ? (
												<Image
													src={getImageUrl(imageName)}
													alt={record.name || "Rich Menu"}
													style={{
														width: "100%",
														height: "100%",
														objectFit: "cover",
														borderRadius: 4,
													}}
													preview={{
														mask: (
															<div
																style={{
																	color: "white",
																	fontSize: 12,
																	fontWeight: "bold",
																}}
															>
																プレビュー
															</div>
														),
													}}
												/>
											) : (
												<div
													style={{
														color: "#bbb",
														fontSize: 12,
														textAlign: "center",
														display: "flex",
														flexDirection: "column",
														alignItems: "center",
														gap: 4,
													}}
												>
													<FileImageOutlined style={{ fontSize: 24 }} />
													<span style={{ fontSize: 10 }}>画像なし</span>
												</div>
											)}
											{record.selected && (
												<div
													style={{
														position: "absolute",
														top: 4,
														right: 4,
														background: "#52c41a",
														color: "white",
														borderRadius: 4,
														padding: "2px 6px",
														fontSize: 10,
														fontWeight: "bold",
														boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
													}}
												>
													SELECTED
												</div>
											)}
										</div>
									),
								},
								{
									title: "メニュー情報",
									key: "info",
									render: (record: any, _, index: number) => (
										<div
											style={{
												display: "flex",
												flexDirection: "column",
												gap: 8,
											}}
										>
											<div
												style={{
													display: "flex",
													alignItems: "center",
													gap: 12,
												}}
											>
												<Text strong style={{ fontSize: 16, color: "#262626" }}>
													{record.name || `Rich Menu ${index + 1}`}
												</Text>
												{record.selected && (
													<Tag color="green" style={{ margin: 0 }}>
														<EyeOutlined style={{ marginRight: 4 }} />
														使用中
													</Tag>
												)}
											</div>
											<div
												style={{
													display: "flex",
													gap: 16,
													alignItems: "center",
												}}
											>
												<div
													style={{
														display: "flex",
														alignItems: "center",
														gap: 4,
													}}
												>
													<Text type="secondary" style={{ fontSize: 12 }}>
														サイズ:
													</Text>
													<Text style={{ fontSize: 12, fontWeight: 500 }}>
														{record.size?.width && record.size?.height
															? `${record.size.width}×${record.size.height}px`
															: "N/A"}
													</Text>
												</div>
												<div
													style={{
														display: "flex",
														alignItems: "center",
														gap: 4,
													}}
												>
													<Text type="secondary" style={{ fontSize: 12 }}>
														ステータス:
													</Text>
													{record.selected ? (
														<Badge status="success" text="アクティブ" />
													) : (
														<Badge status="default" text="非アクティブ" />
													)}
												</div>
												{record.createdAt && (
													<div
														style={{
															display: "flex",
															alignItems: "center",
															gap: 4,
														}}
													>
														<Text type="secondary" style={{ fontSize: 12 }}>
															作成日:
														</Text>
														<Text style={{ fontSize: 12 }}>
															{new Date(record.createdAt).toLocaleDateString(
																"ja-JP",
															)}
														</Text>
													</div>
												)}
											</div>
										</div>
									),
								},
								{
									title: "アクション",
									key: "actions",
									width: 200,
									render: (record: any) => (
										<div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
											<Button
												type="primary"
												icon={<EditOutlined />}
												style={{ fontSize: 12 }}
												onClick={() => handleEdit(tabGroup)}
											>
												編集
											</Button>
											<Button
												icon={<EyeOutlined />}
												style={{
													fontSize: 12,
													borderColor: primaryColor,
													color: primaryColor,
												}}
												onClick={() => {
													messageApi.info("プレビュー機能は準備中です");
												}}
											>
												プレビュー
											</Button>
											<Dropdown
												menu={{
													items: [
														{
															key: "duplicate",
															label: renderDropdownLabel(
																<AppstoreOutlined />,
																"複製",
															),
															onClick: () =>
																handleRichMenuDuplicate(record, tabGroup),
														},
														{
															key: "download",
															label: renderDropdownLabel(
																<SettingOutlined />,
																"ダウンロード",
															),
															onClick: () =>
																handleRichMenuDownload(record, tabGroup),
														},
														{
															type: "divider",
														},
														{
															key: "delete",
															label: renderDropdownLabel(
																<DeleteOutlined />,
																"削除",
															),
															danger: true,
															onClick: () =>
																handleRichMenuDelete(record, tabGroup),
														},
													],
												}}
												placement="bottomRight"
												trigger={["click"]}
											>
												<Button
													icon={<EllipsisOutlined />}
													style={{
														color: primaryColor,
													}}
												/>
											</Dropdown>
										</div>
									),
								},
							]}
						/>
					) : (
						<Empty
							image={Empty.PRESENTED_IMAGE_SIMPLE}
							description="リッチメニューがありません"
							style={{ margin: "20px 0" }}
						/>
					)}
				</div>

				<Divider style={{ margin: "16px 0" }} />

				{/* Metadata */}
				<Row gutter={[16, 8]}>
					<Col span={12}>
						<div style={{ display: "flex", alignItems: "center", gap: 6 }}>
							<Text type="secondary" style={{ fontSize: 12 }}>
								作成日:
							</Text>
							<Text style={{ fontSize: 12 }}>
								{tabGroup.createdAt
									? new Date(tabGroup.createdAt).toLocaleDateString("ja-JP")
									: "N/A"}
							</Text>
						</div>
					</Col>
					<Col span={12}>
						<div style={{ display: "flex", alignItems: "center", gap: 6 }}>
							<Text type="secondary" style={{ fontSize: 12 }}>
								更新日:
							</Text>
							<Text style={{ fontSize: 12 }}>
								{tabGroup.updatedAt
									? new Date(tabGroup.updatedAt).toLocaleDateString("ja-JP")
									: "N/A"}
							</Text>
						</div>
					</Col>
				</Row>
			</Card>
		);
	};

	// Create reusable RichMenuTable component
	const RichMenuTable: React.FC<{
		tabGroups: RichMenuTabGroupWithRichmenus[];
		tabType: "published" | "draft";
	}> = ({ tabGroups, tabType }) => {
		return (
			<div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
				{tabGroups.map((tabGroup) => (
					<div key={tabGroup.groupId} style={{ margin: "0 16px" }}>
						<div>
							{tabGroup.richmenus && tabGroup.richmenus.length > 0 ? (
								tabGroup.richmenus.length === 1 ? (
									// Single richmenu - display directly without tabs
									<Card
										title={tabGroup.richmenus[0].name || "Rich Menu 1"}
										style={{ borderRadius: 8 }}
									>
										<Table
											dataSource={[tabGroup.richmenus[0]]}
											pagination={false}
											rowKey={(record) => record.id || 0}
											showHeader={true}
											scroll={{ x: 800 }}
											columns={getRichMenuColumns(tabGroup)}
										/>
									</Card>
								) : (
									// Multiple richmenus - display as tabs
									<Card
										title={`${tabGroup.name || "Unnamed Group"} (Tabbed Rich Menu)`}
										style={{ borderRadius: 8 }}
									>
										<Tabs
											items={tabGroup.richmenus.map(
												(richMenu: any, index: number) => ({
													key: String(richMenu.id || index),
													label: richMenu.name || `Rich Menu ${index + 1}`,
													children: (
														<Table
															dataSource={[richMenu]}
															pagination={false}
															rowKey={(record) => record.id || index}
															showHeader={true}
															scroll={{ x: 800 }}
															columns={getRichMenuColumns(tabGroup)}
														/>
													),
												}),
											)}
										/>
									</Card>
								)
							) : (
								<Empty
									image={Empty.PRESENTED_IMAGE_SIMPLE}
									description="リッチメニューがありません"
									style={{ margin: "20px 0" }}
								/>
							)}
						</div>
					</div>
				))}
			</div>
		);
	};

	// Define table columns for Rich Menu display
	const getRichMenuColumns = (tabGroup: RichMenuTabGroupWithRichmenus) => [
		{
			title: tabGroup.displayPriority === "USER" ? "登録後" : "登録前",
			key: "image",
			width: 120,
			fixed: "left" as const,
			render: (record: any) => (
				<div
					style={{
						width: "100%",
						maxWidth: 120,
						height: 75,
						border: record.selected ? "2px solid #52c41a" : "1px solid #e8e8e8",
						borderRadius: 6,
						backgroundColor: "#fafafa",
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						position: "relative",
						overflow: "hidden",
					}}
				>
					{record.imageName ? (
						<Image
							src={getImageUrl(record.imageName)}
							alt={record.name || "Rich Menu"}
							style={{
								width: "100%",
								height: "100%",
								objectFit: "cover",
								borderRadius: 4,
							}}
							preview={{
								mask: <div style={{ color: "white", fontSize: 12 }}>拡大</div>,
							}}
						/>
					) : record.templateType ? (
						<div
							style={{
								display: "flex",
								flexDirection: "column",
								alignItems: "center",
								gap: 4,
							}}
						>
							<Image
								src={
									getRichmenuTemplateImage(record.templateType) ||
									`/assets/richmenu-templates/${record.templateType}.png`
								}
								alt={`Rich Menu ${record.name || "Template"}`}
								width={80}
								height={27}
								style={{ borderRadius: 4 }}
							/>
							<Typography.Text
								style={{
									fontSize: 10,
									color: "#666",
									textAlign: "center",
									lineHeight: 1.2,
								}}
							>
								{record.imageName || record.templateType || "No image"}
							</Typography.Text>
						</div>
					) : (
						<div
							style={{
								color: "#bbb",
								fontSize: 12,
								textAlign: "center",
								display: "flex",
								flexDirection: "column",
								alignItems: "center",
								gap: 4,
							}}
						>
							<FileImageOutlined style={{ fontSize: 24 }} />
							<span style={{ fontSize: 10 }}>画像なし</span>
						</div>
					)}
					{record.selected && (
						<div
							style={{
								position: "absolute",
								top: 4,
								right: 4,
								background: "#52c41a",
								color: "white",
								borderRadius: 4,
								padding: "2px 6px",
								fontSize: 10,
								fontWeight: "bold",
							}}
						>
							✓
						</div>
					)}
				</div>
			),
		},
		{
			title: "リッチメニュー名",
			dataIndex: "name",
			key: "name",
			width: 150,
			render: (name: string) => (
				<Typography.Text strong>{name || "未設定"}</Typography.Text>
			),
		},
		{
			title: "チャットバーテキスト",
			dataIndex: "chatBarText",
			key: "chatBarText",
			width: 120,
			render: (text: string) => text || "未設定",
		},
		{
			title: "エリアアクション設定",
			key: "areaActions",
			width: 350,
			render: (record: any) => {
				const areas = record.areas || [];
				const areaActions =
					areas?.map((area: any, areaIndex: number) => ({
						areaIndex: areaIndex + 1,
						actionType: area.action?.type || "unknown",
						actionValue:
							area.action?.uri ||
							area.action?.text ||
							area.action?.data ||
							area.action?.label ||
							"N/A",
						action: area.action,
					})) || [];

				if (areaActions.length === 0) {
					return (
						<div style={{ display: "flex", alignItems: "center", gap: 4 }}>
							<Text type="secondary" style={{ fontSize: 11 }}>
								設定なし
							</Text>
							<Button type="link" style={{ fontSize: 10, padding: 0 }}>
								設定
							</Button>
						</div>
					);
				}

				return (
					<Table
						dataSource={areaActions}
						pagination={false}
						showHeader={true}
						style={{ width: "100%" }}
						rowKey={(_record, index) => `area-action-${index || Math.random()}`}
						scroll={{ y: 120, x: 250 }}
						columns={[
							{
								title: "エリア",
								dataIndex: "areaIndex",
								key: "areaIndex",
								width: 50,
								render: (index: number) => (
									<Text style={{ fontSize: 10, fontWeight: "bold" }}>
										{String.fromCharCode(64 + index)}
									</Text>
								),
							},
							{
								title: "タイプ",
								dataIndex: "actionType",
								key: "actionType",
								width: 80,
								render: (type: string) => (
									<Tag
										color={
											type === "uri"
												? "blue"
												: type === "message"
													? "green"
													: type === "postback"
														? "orange"
														: type === "datetimepicker"
															? "purple"
															: "default"
										}
										style={{ fontSize: 9, margin: 0 }}
									>
										{type}
									</Tag>
								),
							},
							{
								title: "値",
								dataIndex: "actionValue",
								key: "actionValue",
								render: (value: string) => (
									<Text
										style={{
											fontSize: 10,
											maxWidth: 120,
											overflow: "hidden",
											textOverflow: "ellipsis",
											whiteSpace: "nowrap",
											display: "block",
										}}
										title={value}
									>
										{value}
									</Text>
								),
							},
						]}
					/>
				);
			},
		},
		{
			title: "アクション",
			key: "actions",
			width: tabGroup.status === "DRAFT" ? 200 : 120,
			fixed: "right" as const,
			render: () => (
				<div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
					<Button
						type={tabGroup.status === "PUBLISHED" ? "default" : "primary"}
						icon={
							tabGroup.status === "PUBLISHED" ? (
								<EyeInvisibleOutlined />
							) : (
								<EyeOutlined />
							)
						}
						onClick={() =>
							tabGroup.status === "PUBLISHED"
								? handleUnpublish(tabGroup.groupId)
								: handlePublish(tabGroup.groupId)
						}
						loading={publishMutation.isPending || unpublishMutation.isPending}
						style={{ fontWeight: "bold" }}
					>
						{tabGroup.status === "PUBLISHED" ? "非公開" : "公開"}
					</Button>

					{tabGroup.status === "DRAFT" && (
						<>
							<Button
								icon={<EditOutlined />}
								onClick={() => handleEdit(tabGroup)}
							>
								編集
							</Button>
							<Button
								danger
								icon={<DeleteOutlined />}
								onClick={() => handleDelete(tabGroup.groupId)}
							>
								削除
							</Button>
						</>
					)}
				</div>
			),
		},
	];

	// Create the Rich Menu Form component with modern design
	const RichMenuForm = () => {
		if (isLoading) {
			return (
				<div
					style={{
						display: "flex",
						justifyContent: "center",
						alignItems: "center",
						minHeight: 400,
						background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
						borderRadius: 12,
					}}
				>
					<Spin size="large" />
				</div>
			);
		}

		return (
			<div
				style={{ background: "#f8f9fa", minHeight: "100vh", padding: "24px 0" }}
			>
				<div style={{ maxWidth: 1400, margin: "0 auto" }}>
					{/* Header with Title and Action Button */}
					<div
						style={{
							display: "flex",
							justifyContent: "space-between",
							alignItems: "center",
							marginBottom: 24,
							padding: "0 8px",
						}}
					>
						<div>
							<Title level={2} style={{ margin: 0, color: "#262626" }}>
								{title}
							</Title>
							<Text type="secondary" style={{ fontSize: 14 }}>
								リッチメニューの作成・管理・公開を行えます
							</Text>
						</div>
						<Space>
							<Button
								type="primary"
								icon={<PlusOutlined />}
								size="large"
								onClick={handleCreate}
								style={{
									backgroundColor: primaryColor,
									borderColor: primaryColor,
								}}
							>
								新規作成
							</Button>
						</Space>
					</div>

					{/* Main Content Tabs */}
					<Card
						style={{
							borderRadius: 16,
							overflow: "hidden",
							borderColor: primaryColor,
						}}
						styles={{
							header: {
								color: primaryColor,
								backgroundColor: primaryLightColor,
							},
						}}
					>
						<Tabs
							activeKey={activeTabKey}
							onChange={setActiveTabKey}
							size="large"
							style={{ margin: -24 }}
							tabBarStyle={{
								background: "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)",
								margin: 0,
								padding: "0 24px",
							}}
							items={[
								{
									key: "published",
									label: (
										<Space>
											<EyeOutlined />
											公開中
											<Badge
												count={
													defaultTabGroup && perUserTabGroup
														? 2
														: defaultTabGroup || perUserTabGroup
															? 1
															: 0
												}
												showZero
											/>
										</Space>
									),
									children: (
										<div style={{ padding: "24px 0" }}>
											{/* Published Rich Menus Table */}
											<div style={{ marginBottom: 32 }}>
												<div style={{ marginBottom: 16, padding: "0 24px" }}>
													<Title
														level={3}
														style={{ margin: 0, color: "#262626" }}
													>
														公開中のリッチメニュー
													</Title>
													<Text type="secondary" style={{ fontSize: 14 }}>
														現在アクティブなリッチメニューの一覧
													</Text>
												</div>

												{defaultTabGroup || perUserTabGroup ? (
													<RichMenuTable
														tabGroups={[
															...(defaultTabGroup ? [defaultTabGroup] : []),
															...(perUserTabGroup ? [perUserTabGroup] : []),
														]}
														tabType="published"
													/>
												) : (
													<div
														style={{
															padding: "60px 24px",
															textAlign: "center",
														}}
													>
														<Empty
															image={Empty.PRESENTED_IMAGE_SIMPLE}
															description="公開中のリッチメニューはありません"
														>
															<Button
																type="primary"
																icon={<PlusOutlined />}
																onClick={handleCreate}
															>
																新しいリッチメニューを作成
															</Button>
														</Empty>
													</div>
												)}
											</div>
										</div>
									),
								},
								{
									key: "drafts",
									label: (
										<Space>
											<SettingOutlined />
											下書き
											<Badge count={draftTabGroups.length} showZero />
										</Space>
									),
									children: (
										<div style={{ padding: "24px 0" }}>
											{/* Draft Rich Menus Table */}
											<div style={{ marginBottom: 32 }}>
												<div style={{ marginBottom: 16, padding: "0 24px" }}>
													<Title
														level={3}
														style={{ margin: 0, color: "#262626" }}
													>
														下書きのリッチメニュー
													</Title>
													<Text type="secondary" style={{ fontSize: 14 }}>
														公開前のリッチメニューの一覧
													</Text>
												</div>

												{draftTabGroups.length > 0 ? (
													<RichMenuTable
														tabGroups={draftTabGroups}
														tabType="draft"
													/>
												) : (
													<div
														style={{
															padding: "60px 24px",
															textAlign: "center",
														}}
													>
														<Empty
															image={Empty.PRESENTED_IMAGE_SIMPLE}
															description="下書きのリッチメニューはありません"
														>
															<Button
																type="primary"
																icon={<PlusOutlined />}
																onClick={handleCreate}
															>
																新しいリッチメニューを作成
															</Button>
														</Empty>
													</div>
												)}
											</div>
										</div>
									),
								},
							]}
						/>
					</Card>

					{/* Modal */}
					<RichMenuModal
						visible={modalVisible}
						onCancel={() => {
							setModalVisible(false);
							setEditingTabGroup(null);
						}}
						editingTabGroup={editingTabGroup}
						onSuccess={handleModalSuccess}
					/>
				</div>
			</div>
		);
	};

	// Return the new modern page structure
	return (
		<BaseAnimation>
			<RichMenuForm />
		</BaseAnimation>
	);
};
